import { useParams, Link } from "react-router-dom";
import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import { COMPANY } from "@/data/content";

const LAST_UPDATED = "14 May 2026";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal",
    description: "How UK Train Driver Academy Ltd collects, uses and protects personal data.",
    body: [
      {
        h: "Who we are",
        p: `${COMPANY.legalName} ("we", "us" and "our") operates this website and the brands Train Driver Foundation, Train Driver Psychometrics and Railway Careers UK.`,
      },
      {
        h: "Information we collect",
        p:
          "When you complete a contact form or send an enquiry, we may collect the information you provide, including your name, email address, organisation, phone number, enquiry type and message. We only ask for information that helps us respond to your enquiry or provide the service you have requested.",
      },
      {
        h: "How we use your information",
        p:
          "We use the information you provide to respond to your enquiry, provide relevant information about our services, manage customer communications, and maintain appropriate business records. We do not sell your personal data.",
      },
      {
        h: "Lawful basis for processing",
        p:
          "We process personal data where you have given consent, where it is necessary to respond to your enquiry or provide requested services, and where we have a legitimate interest in managing enquiries, customer relationships and service communications.",
      },
      {
        h: "Sharing your information",
        p:
          "We do not sell personal data. We may share information with trusted service providers where necessary to operate this website, respond to enquiries, deliver services, manage communications or comply with legal obligations.",
      },
      {
        h: "Data retention",
        p:
          "We retain enquiry and customer information only for as long as reasonably necessary for the purpose it was collected, including responding to enquiries, providing services, maintaining business records, resolving disputes and complying with legal or regulatory obligations.",
      },
      {
        h: "Your rights",
        p:
          "Under UK data protection law, you may have rights of access, rectification, erasure, restriction, portability and objection. You may also have the right to withdraw consent where processing is based on consent. To exercise your rights, please contact us using the details on our Contact page.",
      },
      {
        h: "Cookies and analytics",
        p:
          "This website may use cookies or similar technologies to support site functionality, improve performance and understand how visitors use the site. Where required, cookie consent will be requested before non-essential cookies are used.",
      },
      {
        h: "Security",
        p:
          "We take reasonable steps to protect personal data from unauthorised access, loss, misuse or disclosure. No website or online service can guarantee complete security, but we aim to use appropriate technical and organisational measures.",
      },
      {
        h: "Changes to this policy",
        p:
          "We may update this Privacy Policy from time to time. The most recent version will be available on this page and will show the date it was last updated.",
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    eyebrow: "Legal",
    description: "Terms of use for the UK Train Driver Academy Ltd website.",
    body: [
      {
        h: "Use of this website",
        p: `This website is provided by ${COMPANY.legalName} for information about our services. By using this website, you agree to use it lawfully, responsibly and in good faith.`,
      },
      {
        h: "Information accuracy",
        p:
          "We aim to keep information accurate and up to date, but we do not guarantee that all content is complete, accurate, current or suitable for every individual or organisation. Website content is provided for general information only.",
      },
      {
        h: "No operational instruction",
        p:
          "Information on this website does not constitute operational instruction, safety-critical authority, legal advice, employment advice or formal railway competence guidance. Users must follow their employer’s authorised procedures, standards and instructions where applicable.",
      },
      {
        h: "Courses and services",
        p:
          "UK Train Driver Academy Ltd provides preparation, education, training and professional development. Our courses and resources do not replace employer training, formal competence assessment, licensing, certification, medical assessment, psychometric assessment or authorised operational instruction.",
      },
      {
        h: "Candidate preparation",
        p:
          "Where services relate to train driver recruitment preparation, psychometric familiarisation, interviews, CVs or applications, we do not guarantee employment, assessment success, interview progression or any specific recruitment outcome.",
      },
      {
        h: "Organisational training",
        p:
          "Organisational training, workshops and development programmes are designed to support learning, reflection and competence development. They do not replace an employer’s safety management system, competence management system, operational rules, internal standards or statutory responsibilities.",
      },
      {
        h: "Third-party links",
        p:
          "This website links to our specialist brand websites and may link to external third-party websites. We are not responsible for the content, availability, policies or practices of external websites.",
      },
      {
        h: "Intellectual property",
        p:
          "All content on this website, unless otherwise stated, is owned by UK Train Driver Academy Ltd or its licensors. You may not copy, reproduce, distribute or adapt website content without written permission, except where permitted by law.",
      },
      {
        h: "Limitation of liability",
        p:
          "To the fullest extent permitted by law, UK Train Driver Academy Ltd is not liable for loss or damage arising from reliance on website content, use of the website, or inability to access the website.",
      },
      {
        h: "Governing law",
        p:
          "These terms are governed by the laws of England and Wales. Any disputes will be subject to the jurisdiction of the courts of England and Wales.",
      },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const content = CONTENT[slug];

  if (!content) {
    return (
      <>
        <SEO
          title="Page Not Found | UK Train Driver Academy Ltd"
          description="The requested legal page could not be found."
          path="/legal"
        />

        <section className="py-32 container-rail text-center">
          <p className="label-eyebrow text-safety">Not found</p>

          <h1 className="mt-4 font-display font-bold text-3xl text-navy">
            Page not found
          </h1>

          <p className="mt-4 text-slate-600">
            The legal page you are looking for does not exist.
          </p>

          <Link
            to="/"
            className="mt-6 inline-flex items-center justify-center text-safety font-semibold hover:underline"
          >
            Back to home
          </Link>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${content.title} | UK Train Driver Academy Ltd`}
        description={content.description}
        path={`/legal/${slug}`}
      />

      <PageHero
        eyebrow={content.eyebrow}
        title={content.title}
        intro={content.description}
      />

      <section
        className="py-20 md:py-24 bg-white"
        data-testid={`legal-${slug}`}
      >
        <div className="container-rail max-w-3xl">
          <div className="space-y-8">
            {content.body.map((section) => (
              <div
                key={section.h}
                className="border-b border-slate-200 pb-6 last:border-0"
              >
                <h2 className="font-display font-bold text-navy text-xl">
                  {section.h}
                </h2>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {section.p}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 border-t border-slate-200 pt-6">
            <p className="text-xs text-slate-500 font-mono-ibm tracking-wider">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}